import { postCategorySchema, type PostCategory } from '@growup/site-shared';
import { Button, Card, Field, Input, Notice } from '@growup/ui';
import { useCallback, useEffect, useId, useState, type FormEvent } from 'react';
import { z } from 'zod';

import { ApiError, getJson, messageOf, send } from '../api';
import { autoSlug } from './PostsView';

/** Categorias das noticias (filtros em growup.gg/noticias). */
export function CategoriesView() {
  const [list, setList] = useState<PostCategory[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    getJson('/api/site/content/categories', z.array(postCategorySchema))
      .then(setList)
      .catch((e: unknown) => setError(messageOf(e)));
  }, []);
  useEffect(load, [load]);

  return (
    <Card headingLevel={2} title="Categorias">
      <div className="flex flex-col gap-4">
        <p className="text-gu-ink-muted">
          Servem de filtro na página de notícias. Só aparecem no site as que têm notícias
          publicadas. Uma categoria com notícias não se apaga.
        </p>
        {error ? <Notice tone="danger">{error}</Notice> : null}
        <ul className="flex flex-col divide-y divide-gu-line">
          {list?.map((c) => (
            <li key={c.id} className="py-4 first:pt-0">
              <CategoryRow category={c} onChanged={load} />
            </li>
          ))}
        </ul>
        <div className="border-t-2 border-gu-line pt-4">
          <CategoryRow category={null} onChanged={load} />
        </div>
      </div>
    </Card>
  );
}

function CategoryRow({
  category,
  onChanged,
}: {
  category: PostCategory | null;
  onChanged: () => void;
}) {
  const [name, setName] = useState(category?.name ?? '');
  const [slug, setSlug] = useState(category?.slug ?? '');
  const [order, setOrder] = useState(String(category?.sortOrder ?? 10));
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const noteId = useId();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setFields({});
    const body = { name, slug, sortOrder: Number(order) || 0 };
    try {
      if (category) await send('PUT', `/api/site/content/categories/${category.id}`, body);
      else {
        await send('POST', '/api/site/content/categories', body);
        setName('');
        setSlug('');
      }
      onChanged();
    } catch (err) {
      setError(messageOf(err));
      if (err instanceof ApiError) setFields(err.fields);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!category || !window.confirm(`Apagar a categoria «${category.name}»?`)) return;
    try {
      await send('DELETE', `/api/site/content/categories/${category.id}`);
      onChanged();
    } catch (err) {
      setError(messageOf(err));
    }
  };

  return (
    <form className="flex flex-col gap-2" onSubmit={(e) => void submit(e)} noValidate>
      {/*
       * Colunas de largura fixa e caixas alinhadas pelo topo: as linhas todas
       * (e a da categoria nova) ficam com as caixas umas por baixo das outras,
       * mesmo quando uma mostra um erro. As notas vao para a linha de baixo,
       * para nao empurrar so uma das caixas.
       */}
      <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_6rem_11rem]">
        <Field label={category ? 'Nome' : 'Nova categoria'} error={fields.name}>
          <Input
            value={name}
            maxLength={60}
            onChange={(e) => {
              setName(e.target.value);
              if (!category) setSlug(autoSlug(e.target.value));
            }}
          />
        </Field>
        <Field label="Endereço" error={fields.slug}>
          <Input value={slug} aria-describedby={noteId} onChange={(e) => setSlug(e.target.value)} />
        </Field>
        <Field label="Ordem" error={fields.sortOrder}>
          <Input inputMode="numeric" value={order} onChange={(e) => setOrder(e.target.value)} />
        </Field>
        <div className="flex flex-col gap-1.5">
          {/* Ocupa o lugar do rotulo, para os botoes ficarem a altura das caixas. */}
          <span
            aria-hidden="true"
            className="hidden font-sub text-label font-bold uppercase sm:invisible sm:block"
          >
            Ações
          </span>
          <div className="flex min-h-11 items-center gap-1">
            <Button type="submit" size="sm" disabled={busy}>
              {category ? 'Guardar' : 'Adicionar'}
            </Button>
            {category ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => void remove()}
              >
                Apagar
              </Button>
            ) : null}
          </div>
        </div>
      </div>
      <p id={noteId} className="text-caption text-gu-ink-muted">
        growup.gg/noticias?categoria={slug || '…'}
        {category ? ` · ${category.posts} notícia${category.posts === 1 ? '' : 's'}` : null}
      </p>
      {error ? <Notice tone="danger">{error}</Notice> : null}
    </form>
  );
}
